import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';

import UpdateIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { styled } from '@mui/material/styles';
import { Box, capitalize, IconButton, MenuItem, Theme } from '@mui/material';

import IconMenu from 'components/IconMenu';
import { EnrichedUserT } from 'business/definitions';
import { includesSSO } from 'helpers/helpers';
import { CellRendererParams } from 'routes/BudgetModule/config/types';
import { useGetResetUrl } from 'routes/Profile/hooks/resetUrl';

import { useRequestResetPassword } from '../../hooks/userActions';
import CreateUserFormProvider from '../../CreateOrEditUser/CreateUserContext/CreateUserFormProvider';
import DeleteUserModal from '../../CreateOrEditUser/DeleteUserModal';
import EditUserModal from '../../CreateOrEditUser/EditUserModal';

type ExtraActionT = {
    label: string;
    onClick: FlowAnyFunction;
};

const commonStyles = ({ theme }: { theme: Theme }) => ({
    color: theme.palette.primary.main,
    margin: '2px',
    padding: '1.5px',
});

const StyIconButton = styled(IconButton, { name: 'StyIconButton' })(
    commonStyles,
);
const StyIconMenu = styled(IconMenu, { name: 'StyIconMenu' })(commonStyles);

type ActionsCellRendererProps = CellRendererParams<
    EnrichedUserT | undefined
> & {
    existingUsersEmails: string[];
};

const ActionsCellRenderer = ({
    value,
    existingUsersEmails,
}: ActionsCellRendererProps): JSX.Element => {
    const { t } = useTranslation();
    const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
    const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);

    const sendEmailMutation = useRequestResetPassword();
    const getResetURLMutation = useGetResetUrl();

    if (!value) return <></>;

    const getActions = (user: EnrichedUserT): ExtraActionT[] =>
        includesSSO(user.authMethods)
            ? [
                  {
                      label: t('adminPanel.sendInviteEmail'),
                      onClick: () => sendEmailMutation.mutate(user.email),
                  },
              ]
            : [
                  {
                      label: user.isActive
                          ? t('adminPanel.resendEmail')
                          : t('adminPanel.sendInviteEmail'),
                      onClick: () => sendEmailMutation.mutate(user.email),
                  },
                  {
                      label: user.isActive
                          ? t('adminPanel.getResetURL')
                          : t('adminPanel.getSetURL'),
                      onClick: () => getResetURLMutation.mutate(user.id),
                  },
              ];

    const actions = getActions(value);
    const otherUsersEmails = existingUsersEmails.filter(
        (email) => value.email !== email,
    );

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <StyIconButton
                    size="small"
                    onClick={() => setIsEditUserModalOpen(true)}
                    className="pendo-update-user-start"
                >
                    <UpdateIcon
                        data-testid={`e2e-modify-user-button-${value?.id}`}
                    />
                </StyIconButton>
                <StyIconButton
                    size="small"
                    onClick={() => setIsDeleteUserModalOpen(true)}
                >
                    <DeleteIcon
                        data-testid={`e2e-delete-user-button-${value.id}`}
                    />
                </StyIconButton>
                {actions.length > 0 && (
                    <StyIconMenu
                        iconElement={<MoreVertIcon />}
                        tooltip={capitalize(t('common.options'))}
                        tooltipPlacement="bottom"
                        small
                    >
                        {actions.map((action) => (
                            <MenuItem
                                key={action.label}
                                onClick={action.onClick}
                            >
                                {action.label}
                            </MenuItem>
                        ))}
                    </StyIconMenu>
                )}
            </Box>
            {isEditUserModalOpen && (
                <CreateUserFormProvider userToUpdate={value}>
                    <EditUserModal
                        otherUsersEmails={otherUsersEmails}
                        isModalOpen={isEditUserModalOpen}
                        setIsModalOpen={setIsEditUserModalOpen}
                        userId={value.id}
                    />
                </CreateUserFormProvider>
            )}
            {isDeleteUserModalOpen && (
                <DeleteUserModal
                    isOpen={isDeleteUserModalOpen}
                    onClose={() => setIsDeleteUserModalOpen(false)}
                    userId={value.id}
                />
            )}
        </>
    );
};

export default ActionsCellRenderer;
